import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Close, Person, Event as EventIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useNavigate } from 'react-router-dom'
import { UserIcon } from 'src/v3/core/icons'
import { useAppSelector } from 'src/store/hooks'
import { selectLightweightUsersV4ByService } from 'src/v3/manage/manage.slice'
import axios from 'src/utilities/http-client'
import type { UserActionData } from '../../types'

dayjs.extend(relativeTime)

// Session response type based on API documentation
interface EventSessionResponse {
  id: string
  name: string
  eventID: string
  sessionTime: string
  endTime?: string
  createdTime?: string
  updatedTime?: string
}

interface UserActionModalProps {
  event: UserActionData | null
  open: boolean
  onClose: () => void
  linkedServiceID: string
}

export default function UserActionModal({
  event,
  open,
  onClose,
  linkedServiceID,
}: UserActionModalProps) {
  let navigate = useNavigate()

  // State management
  let [sessionData, setSessionData] = useState<EventSessionResponse | null>(
    null,
  )
  let [isLoadingSession, setIsLoadingSession] = useState(false)
  let [sessionError, setSessionError] = useState('')
  let [profileImage, setProfileImage] = useState<string>('')

  // Get lightweight users to find basic user info
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID),
  )
  let lightweightUser = lightweightUsers.find((u) => u.id === event?.userID)

  // Fetch profile image
  useEffect(() => {
    if (open && event?.userID) {
      axios
        .get(`/api/v2/manage/profile-image?userID=${event.userID}`)
        .then(({ data }) => data)
        .then(setProfileImage)
        .catch(() => setProfileImage(''))
    }
  }, [open, event?.userID])

  // Fetch session details
  useEffect(() => {
    if (open && event?.sessionID) {
      setIsLoadingSession(true)
      setSessionError('')
      setSessionData(null)

      axios
        .get(`/manage/dashboard/session/${event.sessionID}`)
        .then(({ data }) => {
          setSessionData(data)
        })
        .catch((error) => {
          setSessionError(
            error.response?.data?.message || 'Failed to load session details',
          )
        })
        .finally(() => {
          setIsLoadingSession(false)
        })
    }
  }, [open, event?.sessionID])

  if (!event) {
    return null
  }

  let hasUserInfo = lightweightUser || event

  // Format action type for display
  let formatActionType = (actionType: string) => {
    switch (actionType) {
      case 'event-attended':
        return 'Event Attended'
      case 'event-completed':
        return 'Event Completed'
      default:
        return actionType
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase())
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">User Action Details</Typography>
            <Chip
              label={formatActionType(event.actionType)}
              sx={{
                '&.MuiChip-root': {
                  backgroundColor: (theme) =>
                    `${theme.palette.entityColors.userAction}20`,
                  color: (theme) => theme.palette.entityColors.userAction,
                },
                fontWeight: 500,
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {/* Event Metadata */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {dayjs(event.actionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
            {dayjs(event.actionDate).fromNow()}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {event.eventName}
          </Typography>
        </Box>

        {/* Main Content Grid */}
        <Box className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
          {/* User Information Section */}
          <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <UserIcon />
              User Information
            </Typography>

            {hasUserInfo ? (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  {profileImage ? (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                      }}
                      src={profileImage}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {(
                        lightweightUser?.firstName?.[0] ||
                        event.userName?.[0] ||
                        '?'
                      ).toUpperCase()}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {lightweightUser?.firstName && lightweightUser?.lastName
                        ? `${lightweightUser.firstName} ${lightweightUser.lastName}`
                        : event.userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {lightweightUser?.primaryEmail || event.userEmail}
                    </Typography>
                  </Box>
                </Box>

                {lightweightUser?.licenseTypes &&
                  lightweightUser.licenseTypes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        License Types
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {lightweightUser.licenseTypes.map((licenseType) => (
                          <Chip
                            key={licenseType}
                            label={licenseType}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={() => {
                    onClose()
                    navigate(
                      `/manage/roster/${linkedServiceID}/user/${lightweightUser?.id}`,
                    )
                  }}
                  size="small"
                >
                  View User Profile
                </Button>
              </>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  User information not available
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Session Information Section */}
          <Paper
            sx={{
              p: 2,
              bgcolor: 'background.paperDark',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <EventIcon />
              Session Details
            </Typography>

            {isLoadingSession ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : sessionError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {sessionError}
              </Alert>
            ) : sessionData ? (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Session Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {sessionData.name || 'Unnamed Session'}
                  </Typography>
                </Box>

                <Box className="grid grid-cols-1 gap-3">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Session Time
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(sessionData.sessionTime).format(
                        'MMM D, YYYY [at] h:mm A',
                      )}
                    </Typography>
                  </Box>

                  {sessionData.endTime && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        End Time
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(sessionData.endTime).format(
                          'MMM D, YYYY [at] h:mm A',
                        )}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body2">
                      {sessionData.endTime
                        ? `${dayjs(sessionData.endTime).diff(dayjs(sessionData.sessionTime), 'minutes')} minutes`
                        : 'Zero hour event'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Event ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {sessionData.eventID || event.eventID}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : event.sessionID ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Session information not available
                </Typography>
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No session associated with this action
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Action Details Section */}
        <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Action Details
          </Typography>

          <Box className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            <Box sx={{ p: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Action ID
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}
              >
                {event.actionID}
              </Typography>
            </Box>

            <Box sx={{ p: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Action Type
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatActionType(event.actionType)}
              </Typography>
            </Box>

            <Box sx={{ p: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Event ID
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}
              >
                {event.eventID}
              </Typography>
            </Box>

            {event.sessionID && (
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  Session ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                  }}
                >
                  {event.sessionID}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
